import React from 'react';
import Icon from '../../../components/AppIcon';

const SyncCapabilities = () => {
  const syncFeatures = [
    {
      id: 1,
      icon: "RefreshCw",
      title: "Manual Cloud Sync",
      description: "Sync your data to the cloud whenever internet is available. Choose what data to sync and when to sync it for optimal bandwidth usage.",
      benefits: [
        "On-demand synchronization",
        "Selective data sync options",
        "Bandwidth optimization",
        "Sync status monitoring"
      ]
    },
    {
      id: 2,
      icon: "Database",
      title: "Automatic Backup",
      description: "Daily automated backups ensure your data is always protected. Multiple backup locations including external drives and network storage.",
      benefits: [
        "Scheduled automatic backups",
        "Multiple backup locations",
        "Version history tracking",
        "One-click restoration"
      ]
    },
    {
      id: 3,
      icon: "Cloud",
      title: "Multi-Store Sync (Server Mode)",
      description: "In Server Mode the local server coordinates sync between terminals and the cloud. In Client Mode terminals connect to a remote server over WebSocket for real-time updates when online.",
      benefits: [
        "Cross-store inventory sync",
        "Consolidated reporting",
        "Price consistency",
        "Transfer management"
      ]
    }
  ];

  const syncProcess = [
    {
      step: 1,
      title: "Operate Offline",
      description: "Run your POS completely offline with full functionality",
      icon: "WifiOff"
    },
    {
      step: 2,
      title: "Connect When Ready",
      description: "Connect to internet when convenient for your business - Client Mode uses WebSocket to the server for live sync; Server Mode can push changes to cloud when available.",
      icon: "Wifi"
    },
    {
      step: 3,
      title: "Sync Data",
      description: "Manually trigger sync or let automatic sync handle it",
      icon: "RefreshCw"
    },
    {
      step: 4,
      title: "Continue Offline",
      description: "Disconnect and continue operations without interruption",
      icon: "CheckCircle"
    }
  ];

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-background">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6">
            Flexible Synchronization Options
          </h2>
          <p className="font-body text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
            Work offline with confidence, sync when convenient. Get the best of both worlds with hybrid capabilities.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
          {syncFeatures?.map((feature) => (
            <div
              key={feature?.id}
              className="bg-card rounded-xl border border-border shadow-purple-sm transition-smooth hover:shadow-purple-md p-6 md:p-8"
            >
              <div className="flex items-center justify-center w-14 h-14 bg-primary/10 rounded-xl mb-4 md:mb-6 mx-auto">
                <Icon name={feature?.icon} size={28} color="var(--color-primary)" strokeWidth={2} />
              </div>

              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground text-center mb-3 md:mb-4">
                {feature?.title}
              </h3>

              <p className="font-body text-sm md:text-base text-muted-foreground text-center mb-6">
                {feature?.description}
              </p>

              <ul className="space-y-3">
                {feature?.benefits?.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Icon 
                      name="Check" 
                      size={18} 
                      color="var(--color-success)" 
                      strokeWidth={2}
                      className="flex-shrink-0 mt-0.5"
                    />
                    <span className="font-body text-sm md:text-base text-foreground">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 rounded-2xl border border-primary/20 p-6 md:p-8 lg:p-10">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground text-center mb-8 md:mb-10">
              How Sync Works
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {syncProcess?.map((process, index) => (
                <div key={process.step} className="relative">
                  <div className="flex flex-col items-center text-center">
                    <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-primary text-primary-foreground rounded-full mb-4 shadow-purple-md">
                      <Icon name={process.icon} size={28} color="white" strokeWidth={2} />
                    </div>

                    <div className="absolute top-8 md:top-10 left-1/2 w-full h-0.5 bg-primary/30 -z-10 hidden lg:block" 
                         style={{ display: index === syncProcess?.length - 1 ? 'none' : 'block' }} />

                    <div className="font-heading text-lg md:text-xl font-bold text-primary mb-2">
                      Step {process.step}
                    </div>

                    <h4 className="font-heading text-base md:text-lg font-semibold text-foreground mb-2">
                      {process.title}
                    </h4>

                    <p className="font-body text-sm md:text-base text-muted-foreground">
                      {process.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SyncCapabilities;